/** @format */

import { PROPAGATE_ERROR_TO_SERVER } from './../../server/propagate-error-to-server';
import { searchDropdownValues } from '@plp/data-access/constants';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
	setAllFilters,
	productListResolverFeatureFlagsSet,
	setInitialPageNumber,
	setInitialPageSize,
	setIsProductListPageLoading,
	setPinnedContent,
	setPiqProducts,
	setProductList,
	setSelectedSort,
	setCriteoProducts
} from '@plp/data-access/state';
import {
	ProductListApiService,
	ProductListCompareService,
	ProductListMlService,
	ProductListUIService
} from '@plp/data-access/services';
import { ProductList } from '@plp/data-access/interfaces';
import { catchError, map, take } from 'rxjs/operators';

import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Optional } from '@angular/core';
import { KillswitchService, Logger } from '@shared/data-access/services';

import { Store } from '@ngrx/store';

import { CriteoService } from '@shared/data-access/product-recommendations';
import { of } from 'rxjs';
import { setCommonPageType } from '@state/data-access';
import { SeoService } from '@seo/data-access';
import { ProductTypes } from '@shared/data-access/interfaces';

@Injectable()
export class SearchResultsListPageResolveGuard {
	showPinnedContent = false;
	showPinnedProducts = true;
	showPLPSponsoredProducts = false;
	isUsingCriteoSPAAdServer = false;

	constructor(
		private readonly log: Logger,
		private readonly productListApiService: ProductListApiService,
		private readonly productListUIService: ProductListUIService,
		private readonly killSwitchService: KillswitchService,
		private readonly router: Router,
		private readonly seoService: SeoService,
		private readonly store: Store,
		private readonly title: Title,
		private readonly plpCompareService: ProductListCompareService,
		private readonly plpMlService: ProductListMlService,
		private readonly criteoService: CriteoService,
		@Inject(PLATFORM_ID) private readonly platformId,
		@Optional() @Inject(PROPAGATE_ERROR_TO_SERVER) private readonly propagateToServer
	) {
		this.store.dispatch(setCommonPageType({ pageType: 'srlp' }));
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((data) => {
				this.store.dispatch(productListResolverFeatureFlagsSet({ featureFlags: data }));
				this.showPinnedContent = data.showPinnedContent;
				this.showPinnedProducts = data.showPinnedProducts;
				this.showPLPSponsoredProducts = data.showPLPSponsoredProducts;
				this.isUsingCriteoSPAAdServer = data.isUsingCriteoSPAAdServer;
			});
	}

	resolve(route: ActivatedRouteSnapshot) {
		this.title.setTitle('Search Results');

		const invalidQueryParams = this.productListApiService.searchRequestParamValidator(
			route.queryParams
		);

		const selectedSortNumber: number = parseInt(route.queryParams.selectedSort, 10);

		if (!invalidQueryParams) {
			const selectedSort: { label: string; value: number } = route.queryParams?.selectedSort
				? {
						label: searchDropdownValues[selectedSortNumber].label,
						value: selectedSortNumber
					}
				: { label: searchDropdownValues[0].label, value: 0 };
			this.store.dispatch(setSelectedSort({ selectedSort }));

			const pageNumber = this.productListUIService.getPageNumber(
				route.queryParams?.pageNumber
			);
			const pageSize = this.productListUIService.getPageSize(route.queryParams?.pageSize);
			const filters = this.productListApiService.formatFiltersFromURL(
				route.queryParams.filterFacets
			);
			if (filters) {
				this.store.dispatch(setAllFilters({ selectedFilters: filters }));
			}
			this.store.dispatch(setInitialPageNumber({ pageNumber }));
			this.store.dispatch(setInitialPageSize({ pageSize }));
			this.seoService.createRobotsTag({ value: 'Y' }, { value: 'N' }, { value: 'Y' });

			const queryParams = this.productListApiService.setProductListParamDataForApi(
				null,
				route.queryParams.searchTerm,
				pageNumber,
				filters,
				pageSize,
				route.queryParams.selectedStore,
				route.queryParams.zipCode
			);

			return this.productListApiService
				.getProductListDataFromApi$(queryParams, 1, this.showPinnedProducts)
				.pipe(
					take(1),
					map((productListData: ProductList | null) => {
						this.handlePageRedirect(productListData);

						if (typeof productListData?.totalCount === 'undefined') {
							this.store.dispatch(
								setIsProductListPageLoading({ isProductListPageLoading: false })
							);
							return null;
						}
						this.plpMlService.generateNewMlSearchEventParentUUID();
						this.store.dispatch(setProductList({ productList: productListData }));
						if (!this.plpCompareService.hasTheComparePaintedDoorTestBeenOpened()) {
							this.plpCompareService.getCompareListFromSessionStorage();
						}

						if (this.showPinnedContent) {
							this.productListUIService.setPinnedContentHomrDataToState(
								productListData,
								route.queryParams.searchTerm.replace(/ /g, '_')
							);
						} else {
							this.store.dispatch(setPinnedContent({ pinnedContent: {} }));
						}

						if (this.isUsingCriteoSPAAdServer) {
							// Use Criteo
							this.getAndSetCriteoProducts(
								route.queryParams.searchTerm,
								pageNumber,
								pageSize,
								filters
							);
						} else if (this.showPLPSponsoredProducts) {
							// Fallback to PromoteIQ
							this.productListUIService.setPromoteIQProductsToState(productListData);
						} else {
							this.store.dispatch(setPiqProducts({ piqProducts: [] }));
						}

						return productListData;
					}),
					catchError((error) => {
						if (isPlatformServer(this.platformId) && error?.errorType === 'redirect') {
							throw error;
						}

						this.log.error(
							`Search API error for search term ${
								route.queryParams.searchTerm
							} with the following error: ${this.getErrorToPrint(error)}`
						);
						this.store.dispatch(setProductList({ productList: null }));
						this.store.dispatch(
							setIsProductListPageLoading({ isProductListPageLoading: false })
						);
						return of(null);
					})
				);
		} else {
			this.router.navigateByUrl('/404', { skipLocationChange: false });
		}
	}

	private getAndSetCriteoProducts(
		searchTerm: string,
		pageNumber: number,
		listSize: number,
		filters = null
	): void {
		const criteoFilters =
			filters && Object.keys(filters).length > 0
				? this.criteoService.parseFilters(filters)
				: null;
		this.criteoService
			.viewProductsPage({
				productType: ProductTypes.VIEW_SEARCH_RESULT,
				category: searchTerm,
				listSize,
				pageNumber,
				filters: criteoFilters
			})
			.subscribe({
				next: (res) => {
					this.criteoService.fireBeacon(res?.placements[0]?.carousel[0]?.OnLoadBeacon);
					this.store.dispatch(setCriteoProducts({ criteoProducts: res }));
				},
				error: (err) => {
					console.error('Error fetching Criteo data:', err);
				}
			});
		this.store.dispatch(setCriteoProducts({ criteoProducts: null }));
	}

	// Future resiliency if we move Search to SSR, we want to be able to abort render early for a redirect
	handlePageRedirect(productListData: ProductList | null): void {
		if (isPlatformBrowser(this.platformId) || !productListData?.searchVO?.landingPageURL) {
			return;
		}

		this.propagateToServer({
			errorType: 'redirect',
			redirectUrl: productListData.searchVO.landingPageURL
		});
		throw { errorType: 'redirect', redirectUrl: productListData.searchVO.landingPageURL };
	}

	/**
	 * @description
	 * Accepts an error and stringifies the important parts
	 */
	private getErrorToPrint(error: any): string {
		try {
			return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
		} catch (error) {
			// Swallow the JSON stringify error
			return '';
		}
	}
}
